import { AxiosInstance } from 'axios'

import {
  API_CONSULTATION_GET_APPLICANT,
  API_CONSULTATION_UPDATE_MEMBER,
  API_CONSULTATION_SERVICE_OFFER,
  API_CONSULTATION_HOSPITAL,
  API_CONSULTATION_DOCTOR,
  API_CONSULTATION_REQUEST_AUTH_CODE,
  API_CONSULTATION_REQUEST_AUTH_CODE_BYPASS,
  API_CONSULTATION_TRANSFER_PHONE,
} from './url'

import { buildApiEndpoint, createSolarApiClient } from '@/shared/libs/api-client'

import type {
  ConsultServiceOfferResponse,
  MemberMe,
  RequestAuthCodeCommand,
  ServiceOfferInquirySuccess,
  HospitalInquirySuccess,
  DoctorInquirySuccess,
  TransferPhonePayload,
} from './types'

export class ConsultationService {
  constructor(private readonly apiClient: AxiosInstance) {}

  /**
   * 신청자 정보 조회
   * GET /member/v2/me
   */
  async getApplicant(): Promise<MemberMe> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: API_CONSULTATION_GET_APPLICANT,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<MemberMe>(config)
    return response.data
  }

  /**
   * 인증 코드 요청
   * POST /member/{memberId}/auth-phone 또는 /account/user/phone/send-verification-code
   */
  async requestAuthCode(command: RequestAuthCodeCommand): Promise<{
    succeeded: boolean
    reason?: string
    msgCode?: string
  }> {
    const url =
      command.type === 'bypass'
        ? API_CONSULTATION_REQUEST_AUTH_CODE_BYPASS
        : API_CONSULTATION_REQUEST_AUTH_CODE(command.memberId)

    const config = buildApiEndpoint(
      {
        method: 'POST',
        url,
        data: {
          phoneCountryCode: command.countryCallingCode,
          phoneNational: command.phoneNumber,
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<
      | string
      | {
          succeeded: boolean
          reason?: string
          msgCode?: string
        }
    >(config)

    // @desc: 호출 성공시 백엔드에서 ""가 내려오는데, 통일된 응답 타입으로 구현하기 위해 succeeded 객체를 반환하도록 합니다.
    if (typeof response.data === 'string') {
      return {
        succeeded: true,
      }
    }

    return response.data
  }

  /**
   * 상담 신청
   * POST /consultation/event/{serviceOfferId}
   */
  async requestServiceOfferConsultation(
    serviceOfferId: number,
    data: {
      useVirtualNumber: boolean
      verification?: {
        id: number
        name: string
        countryCallingCode: string
        phoneNumber: string
        verificationCode: string
      }
    },
  ): Promise<ServiceOfferInquirySuccess> {
    // 본인인증이 필요한 경우 먼저 회원 정보 업데이트
    if (data.verification) {
      const updateConfig = buildApiEndpoint(
        {
          method: 'PUT',
          url: API_CONSULTATION_UPDATE_MEMBER(data.verification.id),
          data: {
            name: data.verification.name,
            phoneCountryCode: data.verification.countryCallingCode,
            phoneNational: data.verification.phoneNumber,
            authKey: data.verification.verificationCode,
          },
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )

      await this.apiClient.request<void>(updateConfig)
    }

    // 상담 신청
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: API_CONSULTATION_SERVICE_OFFER(serviceOfferId),
        data: {
          useVn: data.useVirtualNumber,
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    // Solar API 응답 구조: response.data = { reason, msgCode, data: ConsultServiceOfferResponse, succeeded }
    const response = await this.apiClient.request<{
      reason: string
      msgCode: string | null
      data: ConsultServiceOfferResponse
      succeeded: boolean
    }>(config)

    const result = response.data.data

    // 로그 데이터 생성
    return {
      consultationId: result.id,
      airbridgeLogProperties: {
        specialOfferPriceCurrency: result.specialOfferPriceCurrency,
        name: result.eventTitle,
        categoryID: result.treatmentCategoryTagList?.at(0)?.id,
        categoryName: result.treatmentCategoryTagList?.at(0)?.name,
        brandID: result.hospitalInfo?.id,
        brandName: result.hospitalInfo?.name,
        price: result.specialOfferPrice,
        currency: result.specialOfferPriceCurrency,
      },
      amplitudeLogProperties: {
        cost: result.cost,
        eventId: result.eventId,
        eventTitle: result.eventTitle,
        means: result.means,
        origin: result.origin,
        hospital_id: result.hospitalInfo?.id,
        hospital_name: result.hospitalInfo?.name,
        hospital_country: result.hospitalInfo?.country,
        operation_type: result.operationType,
        concern_body_part_tag_name_list: result.amplitudeTreatmentInfo?.concernBodyPartTagNameList,
        concern_body_part_tag_label_list: result.amplitudeTreatmentInfo?.concernBodyPartTagLabelList,
        concern_tag_name_list: result.amplitudeTreatmentInfo?.concernTagNameList,
        concern_tag_label_list: result.amplitudeTreatmentInfo?.concernTagLabelList,
        treatment_id_list: result.amplitudeTreatmentInfo?.treatmentIdList,
        treatment_name_list: result.amplitudeTreatmentInfo?.treatmentNameList,
        treatment_category_tag_id_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagIdList,
        treatment_category_tag_name_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagNameList,
        treatment_category_tag_label_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagLabelList,
        treatment_group_tag_name_list: result.amplitudeTreatmentInfo?.treatmentGroupTagNameList,
        treatment_label_list: result.amplitudeTreatmentInfo?.treatmentLabelList,
        treatment_group_tag_label_list: result.amplitudeTreatmentInfo?.treatmentGroupTagLabelList,
      },
    }
  }

  /**
   * 병원 상담 신청
   * POST /consultation/hospital/{hospitalId}
   */
  async requestHospitalConsultation(
    hospitalId: number,
    data: {
      useVirtualNumber: boolean
      verification?: {
        id: number
        name: string
        countryCallingCode: string
        phoneNumber: string
        verificationCode: string
      }
    },
  ): Promise<HospitalInquirySuccess> {
    // 본인인증이 필요한 경우 먼저 회원 정보 업데이트
    if (data.verification) {
      const updateConfig = buildApiEndpoint(
        {
          method: 'PUT',
          url: API_CONSULTATION_UPDATE_MEMBER(data.verification.id),
          data: {
            name: data.verification.name,
            phoneCountryCode: data.verification.countryCallingCode,
            phoneNational: data.verification.phoneNumber,
            authKey: data.verification.verificationCode,
          },
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )

      await this.apiClient.request<void>(updateConfig)
    }

    // 상담 신청
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: API_CONSULTATION_HOSPITAL(hospitalId),
        data: {
          useVn: data.useVirtualNumber,
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<{
      reason: string
      msgCode: string | null
      data: ConsultServiceOfferResponse
      succeeded: boolean
    }>(config)

    const result = response.data.data

    // 로그 데이터 생성
    return {
      consultationId: result.id,
      airbridgeLogProperties: {
        specialOfferPriceCurrency: result.specialOfferPriceCurrency,
        brandName: result.hospitalInfo?.name ?? '',
        currency: result.specialOfferPriceCurrency,
      },
      amplitudeLogProperties: {
        cost: result.cost,
        eventId: result.eventId,
        eventTitle: result.eventTitle,
        means: result.means,
        origin: result.origin,
        hospital_id: result.hospitalInfo?.id,
        hospital_name: result.hospitalInfo?.name,
        hospital_country: result.hospitalInfo?.country,
        operation_type: result.operationType,
        concern_body_part_tag_name_list: result.amplitudeTreatmentInfo?.concernBodyPartTagNameList,
        concern_body_part_tag_label_list: result.amplitudeTreatmentInfo?.concernBodyPartTagLabelList,
        concern_tag_name_list: result.amplitudeTreatmentInfo?.concernTagNameList,
        concern_tag_label_list: result.amplitudeTreatmentInfo?.concernTagLabelList,
        treatment_id_list: result.amplitudeTreatmentInfo?.treatmentIdList,
        treatment_name_list: result.amplitudeTreatmentInfo?.treatmentNameList,
        treatment_category_tag_id_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagIdList,
        treatment_category_tag_name_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagNameList,
        treatment_category_tag_label_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagLabelList,
        treatment_group_tag_name_list: result.amplitudeTreatmentInfo?.treatmentGroupTagNameList,
        treatment_label_list: result.amplitudeTreatmentInfo?.treatmentLabelList,
        treatment_group_tag_label_list: result.amplitudeTreatmentInfo?.treatmentGroupTagLabelList,
      },
    }
  }

  /**
   * 병원 채팅 상담 신청
   * POST /consultation/hospital/{hospitalId}
   */
  async applyHospitalChatConsultation(
    hospitalId: number,
    data: { memberName: string },
  ): Promise<{
    roomId: string
    hospitalName: string
  }> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: API_CONSULTATION_HOSPITAL(hospitalId),
        headers: {
          deviceType: 'BROWSER',
        },
        data: {
          memberName: data.memberName,
          means: 'CHAT',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<{
      reason: string
      msgCode: string | null
      data: ConsultServiceOfferResponse
      succeeded: boolean
    }>(config)

    const result = response.data.data

    return {
      roomId: result.chatRoomId ?? '',
      hospitalName: result.hospitalInfo?.name ?? '',
    }
  }

  /**
   * 의사 상담 신청
   * POST /consultation/doctor/{doctorId}
   */
  async requestDoctorConsultation(
    doctorId: number,
    data: {
      useVirtualNumber: boolean
      verification?: {
        id: number
        name: string
        countryCallingCode: string
        phoneNumber: string
        verificationCode: string
      }
    },
  ): Promise<DoctorInquirySuccess> {
    // 본인인증이 필요한 경우 먼저 회원 정보 업데이트
    if (data.verification) {
      const updateConfig = buildApiEndpoint(
        {
          method: 'PUT',
          url: API_CONSULTATION_UPDATE_MEMBER(data.verification.id),
          data: {
            name: data.verification.name,
            phoneCountryCode: data.verification.countryCallingCode,
            phoneNational: data.verification.phoneNumber,
            authKey: data.verification.verificationCode,
          },
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )

      await this.apiClient.request<void>(updateConfig)
    }

    // 상담 신청
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: API_CONSULTATION_DOCTOR(doctorId),
        data: {
          useVn: data.useVirtualNumber,
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<{
      reason: string
      msgCode: string | null
      data: ConsultServiceOfferResponse
      succeeded: boolean
    }>(config)

    const result = response.data.data

    // 로그 데이터 생성
    return {
      consultationId: result.id,
      airbridgeLogProperties: {
        specialOfferPriceCurrency: result.specialOfferPriceCurrency,
        name: result.eventTitle,
        brandID: result.hospitalInfo?.id,
        brandName: result.hospitalInfo?.name,
        currency: result.specialOfferPriceCurrency,
      },
      amplitudeLogProperties: {
        cost: result.cost,
        eventId: result.eventId,
        eventTitle: result.eventTitle,
        means: result.means,
        origin: result.origin,
        hospital_id: result.hospitalInfo?.id,
        hospital_name: result.hospitalInfo?.name,
        hospital_country: result.hospitalInfo?.country,
        operation_type: result.operationType,
        concern_body_part_tag_name_list: result.amplitudeTreatmentInfo?.concernBodyPartTagNameList,
        concern_body_part_tag_label_list: result.amplitudeTreatmentInfo?.concernBodyPartTagLabelList,
        concern_tag_name_list: result.amplitudeTreatmentInfo?.concernTagNameList,
        concern_tag_label_list: result.amplitudeTreatmentInfo?.concernTagLabelList,
        treatment_id_list: result.amplitudeTreatmentInfo?.treatmentIdList,
        treatment_name_list: result.amplitudeTreatmentInfo?.treatmentNameList,
        treatment_category_tag_id_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagIdList,
        treatment_category_tag_name_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagNameList,
        treatment_category_tag_label_list: result.amplitudeTreatmentInfo?.treatmentCategoryTagLabelList,
        treatment_group_tag_name_list: result.amplitudeTreatmentInfo?.treatmentGroupTagNameList,
        treatment_label_list: result.amplitudeTreatmentInfo?.treatmentLabelList,
        treatment_group_tag_label_list: result.amplitudeTreatmentInfo?.treatmentGroupTagLabelList,
      },
    }
  }

  /**
   * 번호 이전
   * POST /member/v2/commands/transfer-phone
   * 성공 시: 200, { succeeded: true }
   * 실패 시: 200, { succeeded: false, reason: string, msgCode: string }
   */
  async transferPhone(data: TransferPhonePayload): Promise<{
    succeeded: boolean
    reason?: string
    msgCode?: string
  }> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: API_CONSULTATION_TRANSFER_PHONE,
        data: {
          verificationCode: data.code,
          phoneCountryCode: data.countryCallingCode,
          phoneNational: data.phoneNumber,
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<{
      succeeded: boolean
      reason?: string
      msgCode?: string
    }>(config)

    return response.data
  }
}

// Export configured service instance
export const clientConsultationService = new ConsultationService(createSolarApiClient())
